/**
 * useGame — API wrappers for the prediction game.
 *
 * Deliberately NOT module-cached (unlike useFundamentals/useEarnings): every round is a
 * fresh random draw, so caching would hand the player the same chart twice.
 */
import requests from "../requests";

/** Cap on the client's "already seen" list — mirrors the server's MAX_EXCLUDE. */
const MAX_EXCLUDE = 50;

export interface FetchRoundOptions {
  /** Window key (10/20/65/130/260); omit for any. */
  window?: number | null;
  assetClass?: string | null;
  /** Round ids already played this session, so the server avoids repeats. */
  exclude?: number[];
}

export async function fetchGameRound(opts: FetchRoundOptions = {}): Promise<GameBlindRound> {
  const params: Record<string, string> = {};
  if (opts.window) params.window = String(opts.window);
  if (opts.assetClass) params.asset_class = opts.assetClass;
  if (opts.exclude?.length) params.exclude = opts.exclude.slice(-MAX_EXCLUDE).join(",");

  const res = await requests.get<GameBlindRound>("/api/game/round", { params });
  return res.data;
}

export async function submitGameAnswer(
  token: string,
  guess: GameDirection,
): Promise<GameAnswerResult> {
  const res = await requests.post<GameAnswerResult>("/api/game/answer", { token, guess });
  return res.data;
}

/** GET /api/game/daily — today's fixed 5-round set (or `date`'s, for replaying a past
 * day). Deliberately not cached: it's cheap, `no-store` server-side anyway, and the set
 * is identical across calls for the same date so there's nothing to gain from caching. */
export async function fetchGameDaily(date?: string): Promise<GameDailySet> {
  const params = date ? { date } : undefined;
  const res = await requests.get<GameDailySet>("/api/game/daily", { params });
  return res.data;
}

/**
 * POST /api/game/share — mint a spoiler-free share token for a finished daily set.
 * `guesses` must cover every round in that date's set or the server rejects it; the
 * returned score is computed server-side from real outcomes, not the caller's guesses.
 */
export async function submitGameDailyShare(
  date: string,
  guesses: Record<number, GameDirection>,
): Promise<GameShareResult> {
  const res = await requests.post<GameShareResult>("/api/game/share", { date, guesses });
  return res.data;
}
